/**
 * Step 5 — Summary / Achievement / Commitment (Phase 2 task #7).
 *
 * Per plan §6.1 task #7 + Doc 04 §9:
 *  - "Selesai! [Nama] siap belajar!" headline
 *  - Achievement card with prefs summary (activity count and daily count)
 *    — baris Tema dihapus pada MVP (2026-08-15)
 *    bersamaan dengan penghapusan langkah Quiz Tema.
 *  - Commitment pact UI ("Janji kecil?") with 2 buttons:
 *    "Ya, saya mau!" → commitment=true
 *    "Nanti aja"     → commitment=false
 *  - Primary CTA "Mulai Petualangan" — default to commitment=false
 *    if user hasn't picked either
 *  - Wire to API: POST /v1/quiz/answer (questionKey='commitment',
 *    questionOrder=4, payload { commitment: boolean })
 *  - On complete: `router.replace('/home')` to clear the onboarding
 *    stack (so back from home doesn't return to onboarding)
 *
 * Design notes:
 *  - Preferences are fetched on mount via getPreferences() to render
 *    the achievement card. This is a small extra round-trip but
 *    makes the summary self-correcting (e.g. if user went back and
 *    re-edited a quiz, the latest server state is shown).
 *  - Achievement card mirrors the plan's mockup: subtle purple tint,
 *    1px primary border, 16px radius, 20px padding.
 *  - Commitment card uses cream `#FFF8E7` per plan spec.
 *  - Toast on "Ya" tap: shows "Keren!" briefly before navigating.
 *  - The shell's "Lanjut" header button is HIDDEN — replaced by the
 *    in-content "Mulai Petualangan" CTA. The "Kembali" button
 *    is kept so the user can go back to /quiz-count if needed.
 *  - currentChild is required (and so is a complete prefs row, which
 *    is guaranteed by the quiz screens we just walked through).
 *    Defensive redirect to /child-profile if currentChild is null.
 *  - "Lewati" header is hidden (no implicit skip on the final screen —
 *    the commitment question is the last one and the CTA is always
 *    enabled with a sensible default).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { AppIcon } from '@/components/app-icon';
import { Toast } from '@/components/library/Toast';
import { OnboardingStepShell } from '@/components/onboarding/OnboardingStepShell';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { MISC_ICON } from '@/lib/icons';
import { ApiError } from '@/lib/api/client';
import { submitQuizAnswer } from '@/lib/api/quiz';
import { getPreferences } from '@/lib/api/preferences';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { useOnboarding } from '@/contexts/onboarding-context';
import type { UserPreferences } from '@funpaper/shared';

import { ONBOARDING_ROUTES, getStepByPath } from '@/constants/onboarding';

const TIME_SPENT_CAP_MS = 600_000; // matches server zod cap
const TOAST_DURATION_MS = 1_500;

export default function SummaryScreen() {
  const router = useRouter();
  const step = getStepByPath(ONBOARDING_ROUTES.summary);
  const { currentChild } = useOnboarding();

  const [prefs, setPrefs] = useState<UserPreferences | null>(null);
  const [isLoadingPrefs, setIsLoadingPrefs] = useState(true);
  const [commitment, setCommitment] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const mountTimeRef = useRef<number>(Date.now());
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedRef = useRef(false);

  // Defensive redirect: this screen needs the child profile from step 2.
  useEffect(() => {
    if (!currentChild) {
      router.replace(ONBOARDING_ROUTES.childProfile);
    }
  }, [currentChild, router]);

  // Fetch latest preferences to render the summary. Single GET on mount.
  useEffect(() => {
    if (!currentChild) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getPreferences(currentChild.id);
        if (!cancelled) {
          setPrefs(data);
          setIsLoadingPrefs(false);
        }
      } catch (err) {
        if (!cancelled) {
          setIsLoadingPrefs(false);
          // Non-fatal: the screen still works without prefs (just shows
          // placeholders for the summary).
          setError(
            err instanceof ApiError
              ? err.message
              : 'Gagal memuat ringkasan preferensi.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentChild]);

  // Cleanup toast timer on unmount.
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (finalizeTimerRef.current) clearTimeout(finalizeTimerRef.current);
    };
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, TOAST_DURATION_MS);
  }, []);

  const submitCommitment = useCallback(
    async (value: boolean) => {
      if (isSubmitting) return;
      if (!currentChild) return;

      const timeSpentMs = Math.min(
        Math.max(0, Date.now() - mountTimeRef.current),
        TIME_SPENT_CAP_MS,
      );

      setIsSubmitting(true);
      setError(null);
      try {
        await submitQuizAnswer({
          childProfileId: currentChild.id,
          questionKey: 'commitment',
          questionOrder: 4,
          answerPayload: {
            selected: [], // not used for commitment
            skipped: false,
            timeSpentMs,
            commitment: value,
          },
        });
        setCommitment(value);
        return true;
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : 'Gagal menyimpan preferensi. Coba lagi.';
        setError(message);
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentChild, isSubmitting],
  );

  const finalize = useCallback(() => {
    if (!completedRef.current) {
      completedRef.current = true;
      trackAnalyticsEvent('Onboarding Step Completed', { step_name: 'summary' });
      trackAnalyticsEvent('Onboarding Completed');
    }
    // `router.replace` clears the onboarding stack so back from home
    // doesn't return here. Per plan §9.7.
    // Cast: /home is a Phase 3 route (placeholder today), not in the
    // typed-routes enum yet. The route file exists and is reachable.
    router.replace('/home' as never);
  }, [router]);

  const onTapYa = useCallback(async () => {
    const ok = await submitCommitment(true);
    if (!ok) return;
    showToast('Keren! Worksheet baru akan dikirim setiap pagi jam 7.');
    finalizeTimerRef.current = setTimeout(() => {
      finalizeTimerRef.current = null;
      finalize();
    }, TOAST_DURATION_MS);
  }, [finalize, showToast, submitCommitment]);

  const onTapNanti = useCallback(async () => {
    const ok = await submitCommitment(false);
    if (!ok) return;
    finalize();
  }, [finalize, submitCommitment]);

  const onTapCta = useCallback(async () => {
    // If the user hasn't picked a commitment, default to false.
    if (commitment === null) {
      const ok = await submitCommitment(false);
      if (!ok) return;
      finalize();
      return;
    }
    finalize();
  }, [commitment, finalize, submitCommitment]);

  const onKembali = () => router.back();

  if (!currentChild) {
    return null;
  }

  const activityCount = prefs?.preferredActivity.length ?? 0;
  const dailyCount = prefs?.dailyCount ?? 3;

  return (
    <OnboardingStepShell
      progress={step?.progress ?? 5 / 5}
      stepLabel={step?.stepLabel ?? '5 / 5'}
      onKembali={onKembali}
      // No "Lewati" on the final screen — the commitment question IS
      // the implicit "Lewati" path (via "Nanti aja" or default CTA).
      onLanjut={undefined}
    >
      <ThemedView style={styles.body}>
        <SafeAreaView style={styles.safe} edges={['bottom', 'left', 'right']}>
          <ScrollView
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
          >
            <View style={styles.headlineBlock}>
              <AppIcon source={MISC_ICON.celebration} size={88} />
              <ThemedText type="subtitle" style={styles.title}>
                Selesai! {currentChild.name} siap belajar!
              </ThemedText>
            </View>

            <View style={styles.achievementCard}>
              <ThemedText type="default" style={styles.achievementTitle}>
                Kamu baru aja setting preferensi untuk 1 anak.
              </ThemedText>
              <ThemedText
                type="default"
                themeColor="textSecondary"
                style={styles.achievementBody}
              >
                Mulai besok, {dailyCount} worksheet pilihan khusus untuk{' '}
                <ThemedText type="default" style={styles.inlineBold}>
                  {currentChild.name}
                </ThemedText>{' '}
                akan siap setiap pagi.
              </ThemedText>
              <View style={styles.summaryList}>
                <SummaryRow
                  label="Aktivitas favorit"
                  value={
                    isLoadingPrefs
                      ? '…'
                      : activityCount === 0
                        ? 'Belum dipilih'
                        : `${activityCount} aktivitas`
                  }
                />
                <SummaryRow
                  label="Worksheet per hari"
                  value={
                    isLoadingPrefs ? '…' : `${dailyCount} worksheet`
                  }
                />
              </View>
            </View>

            <View style={styles.commitmentCard}>
              <ThemedText type="default" style={styles.commitmentTitle}>
                Janji kecil?
              </ThemedText>
              <ThemedText
                type="default"
                themeColor="textSecondary"
                style={styles.commitmentBody}
              >
                Kamu mau komitmen untuk buka Funpaper setiap pagi?
              </ThemedText>
              <View style={styles.commitmentButtons}>
                <Pressable
                  onPress={onTapYa}
                  disabled={isSubmitting}
                  accessibilityRole="button"
                  accessibilityLabel="Ya, saya mau komitmen"
                  style={({ pressed }) => [
                    styles.commitmentBtn,
                    styles.commitmentBtnYes,
                    pressed && styles.commitmentBtnPressed,
                  ]}
                >
                  <ThemedText style={styles.commitmentBtnYesText}>
                    Ya, saya mau!
                  </ThemedText>
                </Pressable>
                <Pressable
                  onPress={onTapNanti}
                  disabled={isSubmitting}
                  accessibilityRole="button"
                  accessibilityLabel="Nanti aja"
                  style={({ pressed }) => [
                    styles.commitmentBtn,
                    styles.commitmentBtnNo,
                    pressed && styles.commitmentBtnPressed,
                  ]}
                >
                  <ThemedText style={styles.commitmentBtnNoText}>
                    Nanti aja
                  </ThemedText>
                </Pressable>
              </View>
            </View>

            {error ? (
              <View style={styles.errorBox} accessibilityRole="alert">
                <ThemedText type="small" style={styles.errorText}>
                  {error}
                </ThemedText>
              </View>
            ) : null}
          </ScrollView>

          <View style={styles.footer}>
            <Pressable
              onPress={onTapCta}
              disabled={isSubmitting}
              accessibilityRole="button"
              accessibilityLabel="Mulai Petualangan"
              style={({ pressed }) => [
                styles.cta,
                isSubmitting && styles.ctaDisabled,
                pressed && !isSubmitting && styles.ctaPressed,
              ]}
            >
              <ThemedText style={styles.ctaText}>
                {isSubmitting ? 'Menyimpan…' : 'Mulai Petualangan'}
              </ThemedText>
            </Pressable>
          </View>
        </SafeAreaView>
      </ThemedView>

      <Toast message={toast} />
    </OnboardingStepShell>
  );
}

type SummaryRowProps = {
  label: string;
  value: string;
};

function SummaryRow({ label, value }: SummaryRowProps) {
  return (
    <View style={styles.summaryRow}>
      <ThemedText type="small" themeColor="textSecondary" style={styles.summaryLabel}>
        {label}
      </ThemedText>
      <ThemedText type="small" style={styles.summaryValue}>
        {value}
      </ThemedText>
    </View>
  );
}

const CREAM_BG = '#ffffff';
const ACHIEVEMENT_BG = 'rgba(86, 63, 122, 0.05)';
const COMMITMENT_BG = '#FFF8E7';
const COMMITMENT_BORDER = '#e5e5e5';
const YES_BG = '#f5818f';

const styles = StyleSheet.create({
  body: {
    flex: 1,
  },
  safe: {
    flex: 1,
  },
  content: {
    // Used as ScrollView's contentContainerStyle — needs paddingBottom
    // so the footer (CTA) doesn't cover the last card on long content.
    paddingTop: Spacing.two,
    paddingHorizontal: Spacing.four,
    paddingBottom: Spacing.five,
    gap: Spacing.four,
  },
  headlineBlock: {
    alignItems: 'center',
    gap: Spacing.two,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.two,
  },
  title: {
    textAlign: 'center',
    color: Brand.primary,
  },
  achievementCard: {
    backgroundColor: ACHIEVEMENT_BG,
    borderColor: Brand.primary,
    borderWidth: 1,
    borderRadius: 16,
    padding: Spacing.four,
    gap: Spacing.two,
  },
  achievementTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Brand.primary,
  },
  achievementBody: {
    lineHeight: 22,
  },
  inlineBold: {
    fontWeight: '600',
  },
  summaryList: {
    gap: Spacing.one,
    paddingTop: Spacing.two,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    flex: 1,
  },
  summaryValue: {
    fontWeight: '600',
    color: '#201c23',
  },
  commitmentCard: {
    backgroundColor: COMMITMENT_BG,
    borderColor: COMMITMENT_BORDER,
    borderWidth: 1,
    borderRadius: 16,
    padding: Spacing.four,
    gap: Spacing.three,
  },
  commitmentTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#201c23',
  },
  commitmentBody: {
    lineHeight: 22,
  },
  commitmentButtons: {
    flexDirection: 'row',
    gap: Spacing.two,
  },
  commitmentBtn: {
    flex: 1,
    height: 48,
    borderRadius: Radii.medium,
    alignItems: 'center',
    justifyContent: 'center',
  },
  commitmentBtnYes: {
    backgroundColor: YES_BG,
  },
  commitmentBtnNo: {
    backgroundColor: 'transparent',
  },
  commitmentBtnPressed: {
    opacity: 0.7,
  },
  commitmentBtnYesText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  commitmentBtnNoText: {
    color: '#6b6573',
    fontSize: 15,
    fontWeight: '600',
  },
  errorBox: {
    backgroundColor: 'rgba(229, 62, 62, 0.08)',
    borderColor: 'rgba(229, 62, 62, 0.3)',
    borderWidth: 1,
    borderRadius: Radii.medium,
    paddingVertical: Spacing.two,
    paddingHorizontal: Spacing.three,
  },
  errorText: {
    color: Brand.danger,
    fontWeight: '500',
  },
  footer: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.three,
    paddingBottom: Spacing.four,
    backgroundColor: CREAM_BG,
  },
  cta: {
    backgroundColor: Brand.primary,
    paddingVertical: Spacing.three,
    paddingHorizontal: Spacing.four,
    borderRadius: Radii.medium,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 56,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 4,
  },
  ctaDisabled: {
    backgroundColor: '#d1d1d1',
    shadowOpacity: 0,
    elevation: 0,
  },
  ctaPressed: {
    opacity: 0.9,
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});
